import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import HeroImg from "../../assets/images/Hero-banner.png";
import Alert from "../../assets/images/alert.png";
import "./CommonNewFunnel.css";

const LS_KEY = "glp1_intake_step1";

type FormState = {
  feet: string;
  inches: string;
  weight: string;
  goalWeight: string;
  gender: "" | "Male" | "Female";
  dobMonth: string;
  dobDay: string;
  dobYear: string;

  // derived values
  dob: string; // MM/DD/YYYY
  height: string; // 5' 10"

  Do_any_of_these_apply_to_you_female_conditions: string[];
  Health_Questions_1_Do_any_of_these_apply_to_you: string[];
  Health_Questions_2_Do_any_of_these_apply_to_you: string[];

  Health_Questions_2_medAllergy_option_Info: string;

  Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs:
    | ""
    | "Yes"
    | "No";
  Q1_Please_provide_some_more_information: string;

  Q2_Have_you_had_prior_weight_loss_surgeries: "" | "Yes" | "No";
  Q2_Please_provide_some_more_information: string;

  Q3_Do_you_currently_take_any_prescription_medications: "" | "Yes" | "No";
  Q3_Please_list_the_medications_you_are_prescribed_to: string;

  Blood_pressure_range: string;
  Average_resting_heart_rate: string;
};

const DEFAULT: FormState = {
  feet: "",
  inches: "",
  weight: "",
  goalWeight: "",
  gender: "",
  dobMonth: "",
  dobDay: "",
  dobYear: "",
  dob: "",
  height: "",

  Do_any_of_these_apply_to_you_female_conditions: [],
  Health_Questions_1_Do_any_of_these_apply_to_you: [],
  Health_Questions_2_Do_any_of_these_apply_to_you: [],
  Health_Questions_2_medAllergy_option_Info: "",
  Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs:
    "",
  Q1_Please_provide_some_more_information: "",
  Q2_Have_you_had_prior_weight_loss_surgeries: "",
  Q2_Please_provide_some_more_information: "",
  Q3_Do_you_currently_take_any_prescription_medications: "",
  Q3_Please_list_the_medications_you_are_prescribed_to: "",
  Blood_pressure_range: "",
  Average_resting_heart_rate: "",
};

const FEMALE_CONDITIONS = [
  "Currently or possibly pregnant, or actively trying to become pregnant",
  "Breastfeeding or bottle-feeding with breastmilk",
  "None of the above",
];

const HEALTH_1 = [
  "End-stage kidney disease (on or about to be on dialysis)",
  "End-stage liver disease (cirrhosis)",
  "Current suicidal thoughts and/or prior suicidal attempt",
  "Cancer (active diagnosis, active treatment, or in remission or cancer-free for less than 5 continuous years)",
  "Severe gastrointestinal condition (gastroparesis, blockage, inflammatory bowel disease)",
  "Current diagnosis of or treatment for alcohol, opioid, or substance use disorder/dependence",
  "None of the above",
];

const HEALTH_2 = [
  "Gallbladder disease",
  "Hypertension (high blood pressure)",
  "Seizures",
  "Glaucoma",
  "Sleep apnea",
  "Type 2 diabetes (not on insulin)",
  "Type 2 diabetes (on insulin)",
  "Type 1 diabetes",
  "Diabetic retinopathy (diabetic eye disease)",
  "Use of the blood thinner warfarin (Coumadin/Jantoven)",
  "History of or current pancreatitis",
  "Personal or family history of thyroid cyst/nodule, thyroid cancer, medullary thyroid carcinoma, or MEN2",
  "Gout",
  "High cholesterol or triglycerides",
  "Depression",
  "Head injury",
  "Tumor/infection in brain/spinal cord",
  "Low sodium",
  "Liver disease, including fatty liver",
  "Kidney disease",
  "Elevated resting heart rate (tachycardia)",
  "Coronary artery disease or heart attack/stroke in last 2 years",
  "Allergic to any medication",
  "Congestive heart failure",
  "QT prolongation or other heart rhythm disorder",
  "Hospitalization within the last 1 year",
  "Human immunodeficiency virus (HIV)",
  "Acid reflux",
  "Asthma/reactive airway disease",
  "Urinary stress incontinence",
  "Polycystic ovarian syndrome (PCOS)",
  "Clinically proven low testosterone",
  "Osteoarthritis",
  "Constipation",
  "None of the above",
];

type OptionCardProps = {
  type: "radio" | "checkbox";
  name?: string;
  label: string;
  checked: boolean;
  onChange: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({
  type,
  name,
  label,
  checked,
  onChange,
}) => {
  return (
    <button
      type="button"
      className={`funnel-option funnel-checkbox ${
        checked ? "is-selected" : ""
      }`}
      onClick={onChange}
    >
      <span
        className={`funnel-control ${type} ${checked ? "is-selected" : ""}`}
      >
        {type === "radio" ? (
          <span className="dot" />
        ) : (
          <span className="tick" />
        )}
      </span>

      <span className="funnel-option__label">{label}</span>

      <input
        className="sr-only"
        type={type}
        name={name}
        checked={checked}
        onChange={onChange}
      />
    </button>
  );
};

const Field: React.FC<{
  label: string;
  error?: boolean;
  children: React.ReactNode;
}> = ({ label, error, children }) => {
  return (
    <div className={`funnel-field ${error ? "has-error" : ""}`}>
      <div className="funnel-q-title">{label}</div>
      {children}
    </div>
  );
};

const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M4.51581 7.54794C4.95181 7.10194 5.55881 7.06694 6.09181 7.54794L9.99981 11.2949L13.9078 7.54794C14.4408 7.06694 15.0488 7.10194 15.4818 7.54794C15.9178 7.99294 15.8898 8.74494 15.4818 9.16294C15.0758 9.58094 10.7868 13.6649 10.7868 13.6649C10.5698 13.8879 10.2848 13.9999 9.99981 13.9999C9.71481 13.9999 9.42981 13.8879 9.21081 13.6649C9.21081 13.6649 4.92381 9.58094 4.51581 9.16294C4.10781 8.74494 4.07981 7.99294 4.51581 7.54794Z"
      fill="#38312C"
      fillOpacity="0.2"
    />
  </svg>
);

const ErrorText: React.FC<{ show: boolean; text?: string }> = ({
  show,
  text,
}) => {
  if (!show || !text) return null;
  return (
    <div className="funnel-error">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      {text}
    </div>
  );
};

const WarningBar: React.FC<{ show: boolean; text: string }> = ({
  show,
  text,
}) => {
  if (!show) return null;
  return (
    <div className="funnel-warning">
      <img src={Alert} width="16px" height="16px" alt="" /> {text}
    </div>
  );
};

// helpers
const pad2 = (n: string) => String(n).padStart(2, "0");

const monthToNumber = (monthName: string) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const idx = months.indexOf(monthName);
  return idx >= 0 ? String(idx + 1) : "";
};

const buildDobString = (dobMonth: string, dobDay: string, dobYear: string) => {
  const mm = monthToNumber(dobMonth);
  if (!mm || !dobDay || !dobYear) return "";
  return `${pad2(mm)}/${pad2(dobDay)}/${dobYear}`;
};

const buildHeightString = (feet: string, inches: string) => {
  if (!feet || inches === "") return "";
  return `${feet}' ${inches}"`;
};

const NewFunnelStep1: React.FC = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(DEFAULT);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState(false);

  // ✅ ADD THIS ONLY: clear specific error as soon as user fixes that field
  const clearError = (key: string) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const [hydrated, setHydrated] = useState(false);

  // NOTE: openSelect is now only really used for "dobYear" (year dropdown).
  const [openSelect, setOpenSelect] = useState<null | "dobYear">(null);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 100 }, (_, i) => String(now - i));
  }, []);

  const monthNames = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  // ✅ LOAD
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);

    if (!saved) {
      setHydrated(true);
      return;
    }

    try {
      const parsed = JSON.parse(saved);

      const normalized: Partial<FormState> = {
        ...parsed,
        feet: parsed?.feet ? String(Number(parsed.feet)) : "",
        inches:
          parsed?.inches !== undefined &&
          parsed?.inches !== null &&
          parsed?.inches !== ""
            ? String(Number(parsed.inches))
            : "",
        dobDay: parsed?.dobDay ? String(Number(parsed.dobDay)) : "",
        dobYear: parsed?.dobYear ? String(parsed.dobYear) : "",
      };

      setForm({ ...DEFAULT, ...normalized });
    } catch (err) {
      console.log("Step1 localStorage parse failed:", err, saved);
    } finally {
      setHydrated(true);
    }
  }, []);

  // ✅ DERIVE dob/height AFTER hydration
  useEffect(() => {
    if (!hydrated) return;

    const dob = buildDobString(form.dobMonth, form.dobDay, form.dobYear);
    const height = buildHeightString(form.feet, form.inches);

    if (dob === form.dob && height === form.height) return;

    setForm((p) => ({
      ...p,
      dob,
      height,
    }));
  }, [
    hydrated,
    form.dobMonth,
    form.dobDay,
    form.dobYear,
    form.feet,
    form.inches,
  ]);

  // ✅ SAVE
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS_KEY, JSON.stringify(form));
  }, [form, hydrated]);

  const toggleWithNone = (current: string[], val: string) => {
    const NONE = "None of the above";
    if (val === NONE) return [NONE];
    const withoutNone = current.filter((x) => x !== NONE);
    return withoutNone.includes(val)
      ? withoutNone.filter((x) => x !== val)
      : [...withoutNone, val];
  };

  const toggle = (
    key:
      | "Health_Questions_1_Do_any_of_these_apply_to_you"
      | "Health_Questions_2_Do_any_of_these_apply_to_you"
      | "Do_any_of_these_apply_to_you_female_conditions",
    val: string
  ) => {
    setForm((p) => {
      const nextArr = toggleWithNone(p[key], val);
      const next: FormState = { ...p, [key]: nextArr } as FormState;

      if (key === "Health_Questions_2_Do_any_of_these_apply_to_you") {
        const allergic =
          next.Health_Questions_2_Do_any_of_these_apply_to_you.includes(
            "Allergic to any medication"
          );
        if (!allergic) next.Health_Questions_2_medAllergy_option_Info = "";
      }

      return next;
    });

    // ✅ clear error for that checkbox group when user selects something
    if (key === "Health_Questions_1_Do_any_of_these_apply_to_you") {
      clearError("Health_Questions_1_Do_any_of_these_apply_to_you");
    }
    if (key === "Health_Questions_2_Do_any_of_these_apply_to_you") {
      clearError("Health_Questions_2_Do_any_of_these_apply_to_you");
      // also clear allergy textarea error if needed when selecting allergy option
      // (safe: only removes if exists)
      // note: actual textarea error will clear on typing below too
    }
    if (key === "Do_any_of_these_apply_to_you_female_conditions") {
      clearError("Do_any_of_these_apply_to_you_female_conditions");
    }
  };

  const isValidNumber = (s: string) => /^\d+$/.test(s) && Number(s) > 0;

  const getDobDate = () => {
    if (!form.dobMonth || !form.dobDay || !form.dobYear) return null;

    const monthIndex = monthNames.indexOf(form.dobMonth);
    if (monthIndex < 0) return null;

    const day = Number(form.dobDay);
    const year = Number(form.dobYear);
    if (!Number.isFinite(day) || !Number.isFinite(year)) return null;

    const d = new Date(year, monthIndex, day);
    if (
      d.getFullYear() !== year ||
      d.getMonth() !== monthIndex ||
      d.getDate() !== day
    )
      return null;

    const today = new Date();
    if (d > today) return null;

    return d;
  };

  const calculateAge = (birthDate: Date) => {
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  const validate = () => {
    const e: Record<string, string> = {};

    if (!form.feet) e.feet = "Field is required";
    if (form.inches === "") e.inches = "Field is required";

    if (!form.weight) e.weight = "Field is required";
    else if (!isValidNumber(form.weight))
      e.weight = "Please enter a valid weight";

    if (!form.goalWeight) e.goalWeight = "Field is required";
    else if (!isValidNumber(form.goalWeight))
      e.goalWeight = "Please enter a valid weight";

    if (isValidNumber(form.weight) && isValidNumber(form.goalWeight)) {
      const w = Number(form.weight);
      const gw = Number(form.goalWeight);
      if (gw >= w)
        e.goalWeight = "Goal weight must be less than your current weight.";
    }

    if (!form.gender) e.gender = "Field is required";

    if (!form.dobMonth) e.dobMonth = "Field is required";
    if (!form.dobDay) e.dobDay = "Field is required";
    if (!form.dobYear) e.dobYear = "Field is required";

    if (!e.dobMonth && !e.dobDay && !e.dobYear) {
      const dobDate = getDobDate();
      if (!dobDate) e.dob = "Invalid date of birth. Please enter a valid date.";
      else {
        const age = calculateAge(dobDate);
        if (age < 18 || age > 74)
          e.dob = "You must be between 18 and 74 years old to continue.";
      }
    }

    if (form.gender === "Female") {
      if (!form.Do_any_of_these_apply_to_you_female_conditions?.length) {
        e.Do_any_of_these_apply_to_you_female_conditions =
          "Please select at least one option";
      }
    }

    if (!form.Health_Questions_1_Do_any_of_these_apply_to_you?.length)
      e.Health_Questions_1_Do_any_of_these_apply_to_you =
        "Please select at least one option";

    if (!form.Health_Questions_2_Do_any_of_these_apply_to_you?.length)
      e.Health_Questions_2_Do_any_of_these_apply_to_you =
        "Please select at least one option";

    if (
      !form.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs
    )
      e.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs =
        "Field is required";

    if (!form.Q2_Have_you_had_prior_weight_loss_surgeries)
      e.Q2_Have_you_had_prior_weight_loss_surgeries = "Field is required";

    if (!form.Q3_Do_you_currently_take_any_prescription_medications)
      e.Q3_Do_you_currently_take_any_prescription_medications =
        "Field is required";

    if (!form.Blood_pressure_range)
      e.Blood_pressure_range = "Field is required";
    if (!form.Average_resting_heart_rate)
      e.Average_resting_heart_rate = "Field is required";

    if (
      form.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs ===
        "Yes" &&
      !form.Q1_Please_provide_some_more_information.trim()
    )
      e.Q1_Please_provide_some_more_information = "Field is required";

    if (
      form.Q2_Have_you_had_prior_weight_loss_surgeries === "Yes" &&
      !form.Q2_Please_provide_some_more_information.trim()
    )
      e.Q2_Please_provide_some_more_information = "Field is required";

    if (
      form.Q3_Do_you_currently_take_any_prescription_medications === "Yes" &&
      !form.Q3_Please_list_the_medications_you_are_prescribed_to.trim()
    )
      e.Q3_Please_list_the_medications_you_are_prescribed_to =
        "Field is required";

    if (
      form.Health_Questions_2_Do_any_of_these_apply_to_you.includes(
        "Allergic to any medication"
      ) &&
      !form.Health_Questions_2_medAllergy_option_Info.trim()
    ) {
      e.Health_Questions_2_medAllergy_option_Info = "Field is required";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    setTouched(true);
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate("/glp1/intake-form/step-1");
  };

  const femaleWarning =
    form.gender === "Female" &&
    (form.Do_any_of_these_apply_to_you_female_conditions.includes(
      FEMALE_CONDITIONS[0]
    ) ||
      form.Do_any_of_these_apply_to_you_female_conditions.includes(
        FEMALE_CONDITIONS[1]
      ));

  const Health_Questions_1_Do_any_of_these_apply_to_youWarning =
    form.Health_Questions_1_Do_any_of_these_apply_to_you.length > 0 &&
    !form.Health_Questions_1_Do_any_of_these_apply_to_you.includes(
      "None of the above"
    );

  const Health_Questions_2_Do_any_of_these_apply_to_youWarnList = useMemo(
    () =>
      new Set<string>([
        "Gallbladder disease",
        "Type 2 diabetes (on insulin)",
        "Type 1 diabetes",
        "Diabetic retinopathy (diabetic eye disease)",
        "Use of the blood thinner warfarin (Coumadin/Jantoven)",
        "History of or current pancreatitis",
        "Human immunodeficiency virus (HIV)",
      ]),
    []
  );

  const Health_Questions_2_Do_any_of_these_apply_to_youWarning =
    form.Health_Questions_2_Do_any_of_these_apply_to_you.some((x) =>
      Health_Questions_2_Do_any_of_these_apply_to_youWarnList.has(x)
    );

  const showAllergyTextarea =
    form.Health_Questions_2_Do_any_of_these_apply_to_you.includes(
      "Allergic to any medication"
    );

  const Blood_pressure_rangeWarning =
    form.Blood_pressure_range === "≥140/90 (High Stage 2)";

  // ✅ UI option lists for figma-style radios
  const FEET_OPTIONS = useMemo(() => ["4", "5", "6", "7"], []);
  const INCH_OPTIONS = useMemo(
    () => Array.from({ length: 12 }, (_, i) => String(i)),
    []
  );
  const DAY_OPTIONS = useMemo(
    () => Array.from({ length: 31 }, (_, i) => String(i + 1)),
    []
  );

  return (
    <FunnelLayout>
      <img className="hero-img" src={HeroImg} alt="Hero" />

      <h1 className="title-step-1">
        Reach your goal weight fast{" "}
        <span>without restrictive diets and exercise.</span>
      </h1>

      <p className="subtitle-step-1">
        Please answer the following questions so we can qualify you for medical
        weight loss.
      </p>

      <div className="funnel-section">
        <div className="funnel-q-title">What is your height and weight?</div>

        {/* ✅ FEET (Figma radio list) */}
        <Field label="Feet" error={touched && !!errors.feet}>
          {FEET_OPTIONS.map((v) => (
            <OptionCard
              key={v}
              type="radio"
              name="feet"
              label={v.padStart(2, "0")}
              checked={form.feet === v}
              onChange={() => {
                setForm((p) => ({ ...p, feet: v }));
                clearError("feet");
              }}
            />
          ))}
          <ErrorText show={touched && !!errors.feet} text={errors.feet} />
        </Field>

        {/* ✅ INCHES (Figma radio list) */}
        <Field label="Inches" error={touched && !!errors.inches}>
          {INCH_OPTIONS.map((v) => (
            <OptionCard
              key={v}
              type="radio"
              name="inches"
              label={v}
              checked={form.inches === v}
              onChange={() => {
                setForm((p) => ({ ...p, inches: v }));
                clearError("inches");
              }}
            />
          ))}
          <ErrorText show={touched && !!errors.inches} text={errors.inches} />
        </Field>

        <Field label="Weight (in lbs)" error={touched && !!errors.weight}>
          <input
            className="funnel-input"
            inputMode="numeric"
            placeholder="250"
            value={form.weight}
            onChange={(e) => {
              setForm({ ...form, weight: e.target.value.replace(/\D/g, "") });
              clearError("weight");
            }}
          />
          <ErrorText show={touched && !!errors.weight} text={errors.weight} />
        </Field>

        <Field
          label="What is your goal weight?"
          error={touched && !!errors.goalWeight}
        >
          <input
            className="funnel-input"
            inputMode="numeric"
            placeholder="200"
            value={form.goalWeight}
            onChange={(e) => {
              setForm({
                ...form,
                goalWeight: e.target.value.replace(/\D/g, ""),
              });
              clearError("goalWeight");
            }}
          />
          <ErrorText
            show={touched && !!errors.goalWeight}
            text={errors.goalWeight}
          />
        </Field>
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">Are you male or female?</div>

        {(["Male", "Female"] as const).map((v) => (
          <OptionCard
            key={v}
            type="radio"
            name="gender"
            label={v}
            checked={form.gender === v}
            onChange={() => {
              if (v === "Male") {
                setForm((p) => ({
                  ...p,
                  gender: v,
                  Do_any_of_these_apply_to_you_female_conditions: [],
                }));
              } else {
                setForm((p) => ({ ...p, gender: v }));
              }
              clearError("gender");
            }}
          />
        ))}

        <ErrorText show={touched && !!errors.gender} text={errors.gender} />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">What is your date of birth?</div>

        {/* ✅ MONTH (Figma radio list) */}
        <Field label="Month" error={touched && !!errors.dobMonth}>
          {monthNames.map((m) => (
            <OptionCard
              key={m}
              type="radio"
              name="dobMonth"
              label={m}
              checked={form.dobMonth === m}
              onChange={() => {
                setForm((p) => ({ ...p, dobMonth: m }));
                clearError("dobMonth");
                clearError("dob");
              }}
            />
          ))}
          <ErrorText
            show={touched && !!errors.dobMonth}
            text={errors.dobMonth}
          />
        </Field>

        {/* ✅ DAY (Figma radio list) */}
        <Field label="Day" error={touched && !!errors.dobDay}>
          {DAY_OPTIONS.map((d) => (
            <OptionCard
              key={d}
              type="radio"
              name="dobDay"
              label={d.padStart(2, "0")}
              checked={form.dobDay === d}
              onChange={() => {
                setForm((p) => ({ ...p, dobDay: d }));
                clearError("dobDay");
                clearError("dob");
              }}
            />
          ))}
          <ErrorText show={touched && !!errors.dobDay} text={errors.dobDay} />
        </Field>

        {/* ✅ YEAR stays dropdown (unchanged style) */}
        <Field
          label="Year"
          error={touched && (!!errors.dobYear || !!errors.dob)}
        >
          <div
            className={`funnel-select ${
              openSelect === "dobYear" ? "is-open" : ""
            }`}
          >
            <select
              className="funnel-input"
              value={form.dobYear}
              onFocus={() => setOpenSelect("dobYear")}
              onBlur={() => setOpenSelect(null)}
              onMouseDown={() => setOpenSelect("dobYear")}
              onKeyDown={() => setOpenSelect("dobYear")}
              onChange={(e) => {
                setForm({ ...form, dobYear: e.target.value });
                setOpenSelect(null);
                clearError("dobYear");
                clearError("dob");
              }}
            >
              <option value="" disabled></option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>

            <span className="funnel-select__icon" aria-hidden="true">
              <ChevronIcon />
            </span>
          </div>

          <ErrorText show={touched && !!errors.dobYear} text={errors.dobYear} />
          <ErrorText show={touched && !!errors.dob} text={errors.dob} />
        </Field>
      </div>

      {form.gender === "Female" && (
        <>
          <div className="funnel-divider" />
          <div className="funnel-section">
            <div className="funnel-q-title">Do any of these apply to you?</div>

            {FEMALE_CONDITIONS.map((v) => (
              <OptionCard
                key={v}
                type="checkbox"
                label={v}
                checked={form.Do_any_of_these_apply_to_you_female_conditions.includes(
                  v
                )}
                onChange={() =>
                  toggle("Do_any_of_these_apply_to_you_female_conditions", v)
                }
              />
            ))}

            <ErrorText
              show={
                touched &&
                !!errors.Do_any_of_these_apply_to_you_female_conditions
              }
              text={errors.Do_any_of_these_apply_to_you_female_conditions}
            />

            <WarningBar
              show={femaleWarning}
              text="For your safety, this answer may disqualify you from a prescription"
            />
          </div>
        </>
      )}

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          Health Questions 1: Do any of these apply to you?
        </div>

        {HEALTH_1.map((v) => (
          <OptionCard
            key={v}
            type="checkbox"
            label={v}
            checked={form.Health_Questions_1_Do_any_of_these_apply_to_you.includes(
              v
            )}
            onChange={() => {
              setForm((p) => ({
                ...p,
                Health_Questions_1_Do_any_of_these_apply_to_you: toggleWithNone(
                  p.Health_Questions_1_Do_any_of_these_apply_to_you,
                  v
                ),
              }));
              clearError("Health_Questions_1_Do_any_of_these_apply_to_you");
            }}
          />
        ))}
        <ErrorText
          show={
            touched && !!errors.Health_Questions_1_Do_any_of_these_apply_to_you
          }
          text={errors.Health_Questions_1_Do_any_of_these_apply_to_you}
        />

        <WarningBar
          show={Health_Questions_1_Do_any_of_these_apply_to_youWarning}
          text="For your safety, this answer may disqualify you from a prescription"
        />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          Health Questions 2: Do any of these apply to you?
        </div>

        {HEALTH_2.map((v) => (
          <OptionCard
            key={v}
            type="checkbox"
            label={v}
            checked={form.Health_Questions_2_Do_any_of_these_apply_to_you.includes(
              v
            )}
            onChange={() => {
              toggle("Health_Questions_2_Do_any_of_these_apply_to_you", v);
              clearError("Health_Questions_2_Do_any_of_these_apply_to_you");
            }}
          />
        ))}

        {showAllergyTextarea && (
          <Field
            label="Please provide some more information about your medication allergy"
            error={
              touched && !!errors.Health_Questions_2_medAllergy_option_Info
            }
          >
            <textarea
              className="funnel-input"
              value={form.Health_Questions_2_medAllergy_option_Info}
              onChange={(e) => {
                setForm({
                  ...form,
                  Health_Questions_2_medAllergy_option_Info: e.target.value,
                });
                clearError("Health_Questions_2_medAllergy_option_Info");
              }}
              rows={4}
            />
            <ErrorText
              show={
                touched && !!errors.Health_Questions_2_medAllergy_option_Info
              }
              text={errors.Health_Questions_2_medAllergy_option_Info}
            />
          </Field>
        )}

        <ErrorText
          show={
            touched && !!errors.Health_Questions_2_Do_any_of_these_apply_to_you
          }
          text={errors.Health_Questions_2_Do_any_of_these_apply_to_you}
        />

        <WarningBar
          show={Health_Questions_2_Do_any_of_these_apply_to_youWarning}
          text="For your safety, this answer may disqualify you from a prescription"
        />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          Within the last 3 months, have you taken opiate pain medications
          and/or opiate-based street drugs?
        </div>

        {(["Yes", "No"] as const).map((v) => (
          <OptionCard
            key={v}
            type="radio"
            name="Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs"
            label={v}
            checked={
              form.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs ===
              v
            }
            onChange={() => {
              setForm((p) => ({
                ...p,
                Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs:
                  v,
                Q1_Please_provide_some_more_information:
                  v === "Yes" ? p.Q1_Please_provide_some_more_information : "",
              }));
              clearError(
                "Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs"
              );
            }}
          />
        ))}

        {form.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs ===
          "Yes" && (
          <Field
            label="Please provide some more information"
            error={touched && !!errors.Q1_Please_provide_some_more_information}
          >
            <textarea
              className="funnel-input"
              value={form.Q1_Please_provide_some_more_information}
              onChange={(e) => {
                setForm({
                  ...form,
                  Q1_Please_provide_some_more_information: e.target.value,
                });
                clearError("Q1_Please_provide_some_more_information");
              }}
              rows={4}
            />
            <ErrorText
              show={touched && !!errors.Q1_Please_provide_some_more_information}
              text={errors.Q1_Please_provide_some_more_information}
            />
          </Field>
        )}

        <ErrorText
          show={
            touched &&
            !!errors.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs
          }
          text={
            errors.Q1_Within_the_last_3_months_have_you_taken_opiate_pain_medications_and_or_opiate_based_street_drugs
          }
        />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          Have you had prior weight loss surgeries?
        </div>

        {(["Yes", "No"] as const).map((v) => (
          <OptionCard
            key={v}
            type="radio"
            name="Q2_Have_you_had_prior_weight_loss_surgeries"
            label={v}
            checked={form.Q2_Have_you_had_prior_weight_loss_surgeries === v}
            onChange={() => {
              setForm((p) => ({
                ...p,
                Q2_Have_you_had_prior_weight_loss_surgeries: v,
                Q2_Please_provide_some_more_information:
                  v === "Yes" ? p.Q2_Please_provide_some_more_information : "",
              }));
              clearError("Q2_Have_you_had_prior_weight_loss_surgeries");
            }}
          />
        ))}

        {form.Q2_Have_you_had_prior_weight_loss_surgeries === "Yes" && (
          <Field
            label="Please provide some more information"
            error={touched && !!errors.Q2_Please_provide_some_more_information}
          >
            <textarea
              className="funnel-input"
              value={form.Q2_Please_provide_some_more_information}
              onChange={(e) => {
                setForm({
                  ...form,
                  Q2_Please_provide_some_more_information: e.target.value,
                });
                clearError("Q2_Please_provide_some_more_information");
              }}
              rows={4}
            />
            <ErrorText
              show={touched && !!errors.Q2_Please_provide_some_more_information}
              text={errors.Q2_Please_provide_some_more_information}
            />
          </Field>
        )}

        <ErrorText
          show={touched && !!errors.Q2_Have_you_had_prior_weight_loss_surgeries}
          text={errors.Q2_Have_you_had_prior_weight_loss_surgeries}
        />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          Do you currently take any prescription medications?
        </div>

        {(["Yes", "No"] as const).map((v) => (
          <OptionCard
            key={v}
            type="radio"
            name="Q3_Do_you_currently_take_any_prescription_medications"
            label={v}
            checked={
              form.Q3_Do_you_currently_take_any_prescription_medications === v
            }
            onChange={() => {
              setForm((p) => ({
                ...p,
                Q3_Do_you_currently_take_any_prescription_medications: v,
                Q3_Please_list_the_medications_you_are_prescribed_to:
                  v === "Yes"
                    ? p.Q3_Please_list_the_medications_you_are_prescribed_to
                    : "",
              }));
              clearError("Q3_Do_you_currently_take_any_prescription_medications");
            }}
          />
        ))}

        {form.Q3_Do_you_currently_take_any_prescription_medications ===
          "Yes" && (
          <Field
            label="Please list the medications you are prescribed to"
            error={
              touched &&
              !!errors.Q3_Please_list_the_medications_you_are_prescribed_to
            }
          >
            <textarea
              className="funnel-input"
              value={form.Q3_Please_list_the_medications_you_are_prescribed_to}
              onChange={(e) => {
                setForm({
                  ...form,
                  Q3_Please_list_the_medications_you_are_prescribed_to:
                    e.target.value,
                });
                clearError("Q3_Please_list_the_medications_you_are_prescribed_to");
              }}
              rows={4}
            />
            <ErrorText
              show={
                touched &&
                !!errors.Q3_Please_list_the_medications_you_are_prescribed_to
              }
              text={errors.Q3_Please_list_the_medications_you_are_prescribed_to}
            />
          </Field>
        )}

        <ErrorText
          show={
            touched &&
            !!errors.Q3_Do_you_currently_take_any_prescription_medications
          }
          text={errors.Q3_Do_you_currently_take_any_prescription_medications}
        />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">What is your blood pressure range?</div>

        {[
          "<120/80 (Normal)",
          "120 to 129/<80 (Elevated)",
          "130 to 139/<80-89 (High Stage 1)",
          "≥140/90 (High Stage 2)",
        ].map((v) => (
          <OptionCard
            key={v}
            type="radio"
            name="Blood_pressure_range"
            label={v}
            checked={form.Blood_pressure_range === v}
            onChange={() => {
              setForm({ ...form, Blood_pressure_range: v });
              clearError("Blood_pressure_range");
            }}
          />
        ))}
        <ErrorText
          show={touched && !!errors.Blood_pressure_range}
          text={errors.Blood_pressure_range}
        />

        <WarningBar
          show={Blood_pressure_rangeWarning}
          text="For your safety, this answer may disqualify you from a prescription"
        />
      </div>

      <div className="funnel-divider" />

      <div className="funnel-section">
        <div className="funnel-q-title">
          What is your average resting heart rate?
        </div>

        {[
          "<60 beats per minute (Slow)",
          "60 to 100 beats per minute (Normal)",
          "101 to 110 beats per minute (Slightly Fast)",
          ">110 beats per minute (Fast)",
        ].map((v) => (
          <OptionCard
            key={v}
            type="radio"
            name="hr"
            label={v}
            checked={form.Average_resting_heart_rate === v}
            onChange={() => {
              setForm({ ...form, Average_resting_heart_rate: v });
              clearError("Average_resting_heart_rate");
            }}
          />
        ))}
        <ErrorText
          show={touched && !!errors.Average_resting_heart_rate}
          text={errors.Average_resting_heart_rate}
        />
      </div>

      <button className="next-btn" onClick={next}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep1;